/**
 * ZEcho — Zod validation schemas for the Echo audit logging API.
 *
 * Single source of truth for the Echo HTTP contract. Generates:
 *   - Runtime validation (backend .parse(), frontend response coercion)
 *   - Compile-time TApi types (via InferSchemas)
 *   - React Query hooks (packages/api-codegen)
 *
 * Echo lives in its own Prisma database (packages/prisma-echo), so response
 * rows are validated here rather than re-exported as `DbEntity.EchoLog`.
 */

import { z } from "zod";

import {
  OrganizationEchoConfigPatchSchema,
  OrganizationEchoConfigSchema
} from "../../../shared/echo/config.schemas";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Flat EchoLog row as returned by the controller today — mirrors the
 * prisma-echo schema row exactly, not the nested EchoLogResponse shape.
 * Changing the wire shape is out of scope for this migration.
 */
export const EchoLogRowSchema = z.object({
  id: z.string(),
  timestamp: z.coerce.date(),
  organizationId: z.string(),
  eventId: z.string(),
  eventType: z.string(),
  eventCategory: z.string(),
  eventVersion: z.string(),
  resourceType: z.string(),
  resourceId: z.string().nullable(),
  resourceName: z.string().nullable(),
  actorId: z.string().nullable(),
  actorType: z.string().nullable(),
  actorName: z.string().nullable(),
  actorEmail: z.string().nullable(),
  action: z.string(),
  description: z.string().nullable(),
  changesBefore: z.unknown().nullable(),
  changesAfter: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
  sessionId: z.string().nullable(),
  country: z.string().nullable(),
  region: z.string().nullable(),
  city: z.string().nullable(),
  severity: z.string(),
  status: z.string(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  errorStack: z.string().nullable(),
  complianceTag: z.array(z.string()),
  sensitiveData: z.boolean(),
  redacted: z.boolean(),
  signature: z.string().nullable(),
  durationMs: z.number().nullable(),
  expiresAt: z.coerce.date().nullable(),
  archivedAt: z.coerce.date().nullable()
});

export type EchoLogRow = z.infer<typeof EchoLogRowSchema>;

/**
 * Output format for an export job. Uppercase members mirror the Prisma
 * enum `EchoExportFormat` in packages/prisma-echo/schema.prisma —
 * the literals here MUST stay aligned with that enum.
 */
export const EchoExportFormatSchema = z.enum(["JSON", "CSV", "XLSX", "PDF"]);
export type EchoExportFormat = z.infer<typeof EchoExportFormatSchema>;

/**
 * Lifecycle of a single export job. Uppercase members mirror the Prisma
 * enum `EchoExportStatus`.
 */
export const EchoExportStatusEnumSchema = z.enum(["PENDING", "PROCESSING", "COMPLETED", "FAILED"]);
export type EchoExportStatus = z.infer<typeof EchoExportStatusEnumSchema>;

/**
 * Structured filter grammar for export jobs. Mirrors the subset of filters
 * the Explorer surfaces today; persisted as JSONB on EchoExport.filters
 * so the history row can recreate the exact query post-hoc.
 */
export const EchoExportFiltersSchema = z.object({
  severity: z.array(z.string()).optional(),
  resourceId: z.string().optional(),
  actorId: z.string().optional(),
  status: z.array(z.string()).optional(),
  search: z.string().optional()
});
export type EchoExportFilters = z.infer<typeof EchoExportFiltersSchema>;

// ═══════════════════════════════════════════════════════════════
// CONFIG ENDPOINTS
// ═══════════════════════════════════════════════════════════════

const GetConfigResponseSchema = z.object({
  config: OrganizationEchoConfigSchema.nullable()
});

const UpdateConfigBodySchema = z.object({
  config: OrganizationEchoConfigPatchSchema
});

/**
 * Returns the post-merge config so the caller can hydrate local state from
 * the response without a follow-up GET. Unlike GetConfig, `config` is never
 * null — a PATCH always produces a full, merged OrganizationEchoConfig.
 */
const UpdateConfigResponseSchema = z.object({
  config: OrganizationEchoConfigSchema
});

// ═══════════════════════════════════════════════════════════════
// QUERY LOGS
// ═══════════════════════════════════════════════════════════════

/**
 * Query-string params for `/logs`. Numeric fields use z.coerce.number()
 * because query params always arrive as strings; array fields arrive as
 * `?eventTypes=a&eventTypes=b`, which axios + express both preserve as
 * string[] without coercion.
 */
const QueryLogsParamsSchema = z.object({
  eventTypes: z.array(z.string()).optional(),
  resourceTypes: z.array(z.string()).optional(),
  resourceId: z.string().optional(),
  actorId: z.string().optional(),
  severity: z.array(z.string()).optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(10_000).optional().default(100),
  offset: z.coerce.number().int().min(0).optional().default(0)
});

const QueryLogsResponseSchema = z.object({
  logs: z.array(EchoLogRowSchema),
  total: z.number().int().min(0),
  limit: z.number().int().min(1),
  offset: z.number().int().min(0)
});

// ═══════════════════════════════════════════════════════════════
// STATS
// ═══════════════════════════════════════════════════════════════

const QueueHealthSchema = z.object({
  queue: z.string(),
  waiting: z.number(),
  active: z.number(),
  completed: z.number().optional(),
  failed: z.number(),
  delayed: z.number().optional(),
  total: z.number().optional(),
  healthy: z.boolean()
});

const GetStatsResponseSchema = z.object({
  database: z.object({
    logs: z.number(),
    exports: z.number(),
    healthy: z.boolean()
  }),
  queues: z.object({
    primary: QueueHealthSchema,
    failed: QueueHealthSchema
  }),
  processing: z.object({
    recovery: z.object({
      recovered: z.number(),
      failedRecovery: z.number(),
      /**
       * P2002 duplicates the recovery processor swallowed — the primary
       * reactor already wrote the event before the broker ack was lost.
       * Not included in `recovered` (which counts real rescues). Ops
       * should alert when this climbs rapidly — it signals Bull is
       * re-delivering jobs the primary already handled.
       */
      duplicatesSkipped: z.number(),
      successRate: z.number()
    })
  })
});

// ═══════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════

/**
 * Flat EchoExport row as returned by list/status endpoints. Shape mirrors
 * the prisma-echo row, except:
 *   - `sizeBytes` is number (bigint → number at the controller boundary)
 *   - `filters` is the structured Zod shape, not raw Json
 *
 * Kept as one schema so ListExports and GetExportStatus don't drift.
 */
export const EchoExportRowSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  requesterUserId: z.string().nullable(),
  format: EchoExportFormatSchema,
  s3Key: z.string().nullable(),
  startTime: z.coerce.date(),
  endTime: z.coerce.date(),
  eventTypes: z.array(z.string()),
  resourceTypes: z.array(z.string()),
  selectedColumns: z.array(z.string()),
  filters: EchoExportFiltersSchema.nullable(),
  status: EchoExportStatusEnumSchema,
  recordCount: z.number().int().nullable(),
  sizeBytes: z.number().nullable(),
  createdAt: z.coerce.date(),
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  expiresAt: z.coerce.date().nullable(),
  errorMessage: z.string().nullable()
});

export type EchoExportRow = z.infer<typeof EchoExportRowSchema>;

/**
 * Request body for creating a new export job.
 *
 * `destination` is intentionally absent — the server derives the S3 key
 * from (organizationId, exportId, format) so callers cannot write to
 * arbitrary buckets / keys. Pre-signed downloads gate access on read.
 *
 * `selectedColumns` is the user's subset of the 26 EchoLog columns; empty
 * array means "all columns" and is expanded server-side.
 */
const CreateExportBodySchema = z.object({
  format: EchoExportFormatSchema,
  startTime: z.string(),
  endTime: z.string(),
  eventTypes: z.array(z.string()).optional(),
  resourceTypes: z.array(z.string()).optional(),
  selectedColumns: z.array(z.string()).optional().default([]),
  filters: EchoExportFiltersSchema.optional()
});

const CreateExportResponseSchema = z.object({
  exportId: z.string(),
  status: z.literal("PENDING")
});

const GetExportStatusResponseSchema = EchoExportRowSchema;

const ListExportsParamsSchema = z.object({
  status: EchoExportStatusEnumSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0)
});

const ListExportsResponseSchema = z.object({
  exports: z.array(EchoExportRowSchema),
  total: z.number().int().min(0),
  limit: z.number().int().min(1),
  offset: z.number().int().min(0)
});

/**
 * Pre-signed S3 GET URL for a completed export. Short TTL (issued with a
 * few-minutes expiry); callers must not persist the URL. The response also
 * echoes a suggested download filename so the browser Save As dialog shows
 * something human-readable.
 */
const GetDownloadUrlResponseSchema = z.object({
  url: z.string(),
  filename: z.string(),
  expiresAt: z.coerce.date()
});

// ═══════════════════════════════════════════════════════════════
// RESOLVE ACTORS
// ═══════════════════════════════════════════════════════════════

const ResolveActorsBodySchema = z.object({
  actorIds: z.array(z.string())
});

const ResolvedActorSchema = z.object({
  name: z.string(),
  email: z.string().nullable()
});

const ResolveActorsResponseSchema = z.object({
  actors: z.record(z.string(), ResolvedActorSchema)
});

// ═══════════════════════════════════════════════════════════════
// HEALTH
// ═══════════════════════════════════════════════════════════════

const HealthCheckResponseSchema = z.object({
  status: z.enum(["healthy", "unhealthy"]),
  database: z.boolean(),
  queue: z.boolean(),
  timestamp: z.coerce.date()
});

// ═══════════════════════════════════════════════════════════════
// Z OBJECT
// ═══════════════════════════════════════════════════════════════

export const ZEcho = {
  GetConfig: {
    method: "GET" as const,
    path: "/api/echo/config",
    Response: ApiSuccessSchema(GetConfigResponseSchema)
  },
  UpdateConfig: {
    method: "PATCH" as const,
    path: "/api/echo/config",
    Body: UpdateConfigBodySchema,
    Response: ApiSuccessSchema(UpdateConfigResponseSchema)
  },
  QueryLogs: {
    method: "GET" as const,
    path: "/api/echo/logs",
    Params: QueryLogsParamsSchema,
    Response: ApiSuccessSchema(QueryLogsResponseSchema)
  },
  GetStats: {
    method: "GET" as const,
    path: "/api/echo/stats",
    Response: ApiSuccessSchema(GetStatsResponseSchema)
  },
  CreateExport: {
    method: "POST" as const,
    path: "/api/echo/export",
    Body: CreateExportBodySchema,
    Response: ApiSuccessSchema(CreateExportResponseSchema)
  },
  GetExportStatus: {
    method: "GET" as const,
    path: "/api/echo/export/:id",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(GetExportStatusResponseSchema)
  },
  ListExports: {
    method: "GET" as const,
    path: "/api/echo/exports",
    Params: ListExportsParamsSchema,
    Response: ApiSuccessSchema(ListExportsResponseSchema)
  },
  GetDownloadUrl: {
    method: "GET" as const,
    path: "/api/echo/export/:id/download",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(GetDownloadUrlResponseSchema)
  },
  ResolveActors: {
    method: "POST" as const,
    path: "/api/echo/resolve-actors",
    Body: ResolveActorsBodySchema,
    Response: ApiSuccessSchema(ResolveActorsResponseSchema)
  },
  HealthCheck: {
    method: "GET" as const,
    path: "/api/echo/health",
    Response: ApiSuccessSchema(HealthCheckResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// INFERRED TYPES
// ═══════════════════════════════════════════════════════════════

export type QueryLogsParams = z.infer<typeof QueryLogsParamsSchema>;
export type QueryLogsResponse = z.infer<typeof QueryLogsResponseSchema>;
export type GetStatsResponse = z.infer<typeof GetStatsResponseSchema>;
export type CreateExportBody = z.infer<typeof CreateExportBodySchema>;
export type CreateExportResponse = z.infer<typeof CreateExportResponseSchema>;
export type GetExportStatusResponse = z.infer<typeof GetExportStatusResponseSchema>;
export type ListExportsParams = z.infer<typeof ListExportsParamsSchema>;
export type ListExportsResponse = z.infer<typeof ListExportsResponseSchema>;
export type GetDownloadUrlResponse = z.infer<typeof GetDownloadUrlResponseSchema>;
export type ResolveActorsBody = z.infer<typeof ResolveActorsBodySchema>;
export type ResolveActorsResponse = z.infer<typeof ResolveActorsResponseSchema>;
export type HealthCheckResponse = z.infer<typeof HealthCheckResponseSchema>;
